import {
  equals,
  find,
  propEq,
  isNil,
  path,
  pathOr,
  all,
  has,
  any,
  curry,
  filter,
  length,
  map,
  not,
  curryN,
  cond,
  T,
  pipeWith,
  always,
  includes,
} from 'ramda';
import { isFunction } from 'src/utilities/isFunction';
import { convertorFunctions } from 'src/components/convertor';
import { conditionalFuncs } from 'src/utilities/conditionalFn';
import { isEmptyOrNil, isNotEmpty, propIsNotEmpty } from 'src/utilities/hasValue';
import { filterIndexed } from 'src/utilities/visualFieldsMapper';
import { isArray } from 'src/utilities/types';
import { parsePathValue } from 'src/features/core/ChatEngine/Configs/autoAnswerConfigurator';

export const resourceFilterConditionExecutor = (iteratorConf: any, resources: any) => {
  const conditions = pathOr([], ['filter_option', 'conditions'], iteratorConf)
  const applyLogicalCondition = pathOr('', ['filter_option', 'apply_logical'], iteratorConf)
  // If the conditions is empty then its false by default
  if (!isNotEmpty(conditions)) {
    return resources
  }
  const result = cond([
    [equals('pipe'), () => resourcesFilterPipe(resources, conditions)],
    [T, () => applyFilterWithDefaultLogic(conditions, applyLogicalCondition, resources)],
  ])(applyLogicalCondition)
  return result;
}

const resourceFilterCondition = (condition: any) => {
  return (resources: any) => filterIndexed((resource: any, index: number) => {
    return equals(true, executeFilterCondition(condition, resource, index))
  }, resources)
}

const resourcesFilterPipe = (resources: any, conditions: any) => {
  const fns: any = map((c: any) => resourceFilterCondition(c))(conditions)
  const pipeWhileNotNil = pipeWith((f, res) => isNil(res) ? res : f(res));
  return pipeWhileNotNil(fns)(resources)
}

const applyFilterWithDefaultLogic = (conditions: any, applyLogicalCondition: any, resources: any) => {
  const result = filterIndexed((resource: any, index: number) => {
    const rules = map(
      (condition: any) => executeFilterCondition(condition, resource, index)
    )(conditions)
    const fn = equals(applyLogicalCondition, 'and') ? all(equals(true)) : any(equals(true))
    return fn(rules)
  }, resources || []);

  return result;
}





const executeFilterCondition = (cond: any, resource: any, resourceIndex: number) => {
  const condType = pathOr('', ['satisfies'], cond)
  const args = evalFilterArguments(cond, resource, resourceIndex)
  const result = curry(conditionalFuncs(condType))(...args)
  return isFunction(result) ? false : !!result;

}
/**
displayerConditionExecutor is used to execute the given
condition has meet true that condition is based on displayer value
for example:
apply_logical: default is or, if we send 'and' then validate is all condition return true
{
arguments: args is array of json that can have displayer <displayer_name> or target_value (static value)
to evaluate the satisfies.
    (if the displayer name has 'this' it will denote the same displayer)

satisfies: (...args) => bool supported validator methods will get displayer value and return true or false
}
If we have configured more that one condition then it will apply and operator on each condtion
  disabled_when: {
  apply_logical: 'and'
  conditions: [
  {
    satisfies: isNotEmpty
    arguments : [
    {
      'displayer':'this'
    }
  ]
  },
  {
    satisfies: equals
    arguments : [{
      'displayer':'refinance_type',
    },
    {
    'target_value':'No Cash-Out Refi'
  }]
  }
]
}

now we are apply this on disabled_when
* @param displayers { list } of displayers json
* @param thisDisplayer { object } displayer which condition is going to be execute
* @param configKey { string } config key to find Condition is on which path in displayer (
  so it can apply for any config which has apply logic with conditions 
  like: 
  disabled_when,
  showWhen
)
*/

export const displayerConditionExecutor = (
  displayers: any,
  thisDisplayer: any,
  configKey: string = 'disabled_when'
) => {
  const conditions = pathOr([], [configKey, 'conditions'], thisDisplayer)
  const applyLogicalCondition = pathOr('', [configKey, 'apply_logical'], thisDisplayer)
  return executeConditions(
    displayers,
    thisDisplayer,
    conditions,
    applyLogicalCondition
  )
}

/**
 * executeConditions executes the given conditions configs and 
* @param displayers { list } of displayers json
* @param thisDisplayer { object } displayer which condition is going to be execute
 * returns True if all the give condition statisfies when 'and' logic is applied other wise return false
 * returns True if any one of the given condition statisfies when 'any' logic is applied  other wise return false
*/
export const executeConditions = (
  displayers: any,
  thisDisplayer: any,
  conditions: any,
  applyLogicalCondition: string
) => {
  // If the conditions is empty then its false by default
  if (isEmptyOrNil(conditions)) {
    return false
  }
  const rules = map(
    (condition: any) => executeCondition(displayers, thisDisplayer, condition)
  )(conditions)
  const fn = equals(applyLogicalCondition, 'and') ? all(equals(true)) : any(equals(true))

  return fn(rules)
}

const executeCondition = (displayers: any, thisDisplayer: any, cond: any) => {
  const condType = pathOr('', ['satisfies'], cond)
  const args = evalArguments(displayers, thisDisplayer, cond)
  if (length(args) <= 0) {
    return false
  }
  const result = curry(conditionalFuncs(condType))(...args)
  return isFunction(result) ? false : !!result;
}

export const findDisplayerOfTheConditionArgument = (arg: any,  thisDisplayer: any, displayers: any) => {
  const additionalDisplayer = find((item: any) => equals(pathOr('', ['displayer_value'], arg), pathOr('', ['match'], item)), displayers)

  const displayer = cond([
    [propEq('displayer', 'this'), always(thisDisplayer)],
    [has('displayer_value'), always(additionalDisplayer)],
    [has('displayer_match'), () => {
      return filter(
        propIsNotEmpty('value'),
        filter(
          (dis: any) => isNotEmpty(dis?.match) && includes(pathOr('', ['displayer_match'], arg), dis.match),
          displayers
        )
      )
    }],
    [T, always(find((dis: any) => equals(dis.match, arg.displayer))(displayers))]
  ])(arg)
  return displayer

} 

const findDisplayerValue = (arg: any, displayers: any, thisDisplayer: any) => {
  if (has('target_value', arg)) {
    return path(['target_value'], arg)
  }
  const displayer = findDisplayerOfTheConditionArgument(arg, thisDisplayer, displayers)
  return isArray(displayer) ? map(path(['value']), displayer) : path(['value'], displayer) 
}

const evalArguments = (displayers: any, thisDisplayer: any, cond: any) => {
  return map(
    (arg: any) => findDisplayerValue(arg, displayers, thisDisplayer),
    pathOr([], ['arguments'], cond)
  )
}

export const evalFilterArguments = (cond: any, resource: any, resourceIndex: number) => {

  const argmts = pathOr([], ['arguments'], cond)
  const isTargetValue = has('target_value')
  const isResourceIndex = has('resource_index')
  return map((arg: any) => {
    if (isResourceIndex(arg)) {
      return resourceIndex
    }
    if (isTargetValue(arg)) {
      return path(['target_value'], arg)
    }
    if (has('resource_index')(arg)) {
      return resourceIndex;
    }
    return parsePathValue(pathOr('', ['data_path'], arg), resource)
  })(argmts)

}


export const executeConvertors = (displayers: any, thisDisplayer: any) => {
  const cond = pathOr({}, ['convertor', 'conditions', 0], thisDisplayer)
  if (cond) {
    return executeConvertor(displayers, thisDisplayer, cond)
  }
  return pathOr('', ['value'], thisDisplayer)
}

const executeConvertor = (displayers: any, thisDisplayer: any, cond: any) => {
  const condFn = pathOr('', ['convertor_name'], cond)
 

  const args = evalArguments(displayers, thisDisplayer, cond)
  if (length(args) <= 0) {
    return pathOr('', ['value'], thisDisplayer)
  }
  const result = curry(convertorFunctions(condFn))(...args)
  return isFunction(result) ?
    pathOr('', ['value'], thisDisplayer) :
    result;
}

const executeSetterConvertor = (displayers: any, thisDisplayer: any, setter: any) => {
  const condFn:any = pathOr('', ['convertor'], setter)
  const args: any = evalArguments(displayers, thisDisplayer, setter)
  if (!isNotEmpty(args)) {
    return pathOr('', ['value'], thisDisplayer)
  }
 /**
   * Note: `curryN` in Ramda supports up to 10 arguments by design.
   * To handle cases where the number of arguments exceeds this limit,
   * we wrap the arguments into a single array. The `wrappedFunction` is
   * defined to accept an array of arguments and spread them when invoking
   * the target function (`convertorFunctions(condFn)`).
   */  
  const wrappedFunction = (argsArray: any[]) =>
    convertorFunctions(condFn)(...argsArray);

  const result = curryN(1, wrappedFunction)(args); // Arity fixed to 1
  return isFunction(result) ? null : result;
}


export const evaluateSetterValueWhen = (displayers: any, thisDisplayer: any) => {
  const values = map((setter: any) => {
    return {
      setter,
      value: executeSetterConvertor(displayers, thisDisplayer, setter)
    }
  }
  )(pathOr([], ['value_setter_when', 'setters'], thisDisplayer))

  const satisfiedValues = filter((data: any) => {
    const conditions = pathOr([], ['setter', 'conditions'], data)
    const applyLogicalCondition = pathOr('', ['setter', 'apply_logical'], data)

    const isSatisfies = executeConditions(
      displayers,
      thisDisplayer,
      conditions,  
      applyLogicalCondition
    )
    // We can't use isNotEmpty because some case value can be empty values like '', [] 
    return not(equals(data.value, null)) && isSatisfies
  })(values)

  if (isNotEmpty(satisfiedValues)) {
    return pathOr(null, [0, 'value'], satisfiedValues)
  }

  return null

}


export const isDeactivatedUponByDisplayers = (
  displayers: any[],
  deactivatedUpon: any
) => {
  const conditions = pathOr([], ['conditions'], deactivatedUpon)
  const applyLogicalCondition = pathOr('', ['apply_logical'], deactivatedUpon)

  const isSatisfies = executeConditions(
    displayers,
    {},
    conditions,
    applyLogicalCondition
  )

  return isSatisfies
}